import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import MuiRefreshIcon from '@mui/icons-material/Refresh';

import type { StarPrinterStatus } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

// ----------------------------------------------------------------------

interface PrinterStatusCardProps {
  status: StarPrinterStatus | null;
  connected: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}

export default function PrinterStatusCard({
  status,
  connected,
  refreshing,
  onRefresh,
}: PrinterStatusCardProps) {
  const chips: { label: string; color: 'success' | 'error' | 'warning' | 'default' }[] = [
    connected
      ? { label: 'Connected', color: 'success' }
      : { label: 'Disconnected', color: 'default' },
  ];

  if (status) {
    chips.push(
      status.online ? { label: 'Online', color: 'success' } : { label: 'Offline', color: 'error' }
    );
    if (status.coverOpen) chips.push({ label: 'Cover open', color: 'warning' });
    if (status.paperEmpty) chips.push({ label: 'No paper', color: 'error' });
    else if (status.paperNearEmpty) chips.push({ label: 'Low paper', color: 'warning' });
    if (status.cutterError) chips.push({ label: 'Cutter error', color: 'error' });
    if (status.cashDrawerOpen ?? status.drawerOpen)
      chips.push({ label: 'Drawer open', color: 'default' });
  }

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1">Printer Status</Typography>
        <LoadingButton
          size="small"
          variant="outlined"
          loading={refreshing}
          onClick={onRefresh}
          startIcon={<MuiIcon icon={MuiRefreshIcon} />}
        >
          Refresh
        </LoadingButton>
      </Stack>

      {status || connected ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chips.map((chip) => (
            <Chip
              key={chip.label}
              label={chip.label}
              color={chip.color}
              size="small"
              variant="soft"
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No status yet. Select a printer and refresh to read its status.
        </Typography>
      )}
    </Card>
  );
}
